import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, X } from 'lucide-react';
import {
  getNextSteps,
  stageLabel,
  IMPROVEMENT_STAGES,
  JOURNEY_STORAGE_KEY,
  JOURNEY_DECLARED_KEY,
} from '../../data/improvementJourney';
import { resolveJourney, recordStageSignal } from '../../data/journeyResolver';
import { useFavourites } from '../../context/FavouritesContext';
import { useMarketingAuth } from '../../hooks/useMarketingAuth';
import { useAuthModal } from '../../context/AuthModalContext';

/**
 * JourneyResume - a "pick up where you left off" prompt.
 *
 * Reads the anonymous journey memory (last project + stage, stored in
 * localStorage by JourneyNextStep) and the saved-guides count from the
 * localStorage-backed FavouritesContext. When a returning visitor has a
 * remembered project or saved guides, it offers a one-tap way back into
 * their next step, plus a soft bridge to a free account (which merges
 * their anonymous saves server-side on sign-up).
 *
 * Self-hides when there is nothing to resume. Dismissible for the
 * session so it never nags.
 */
const DISMISS_KEY = 'bp:journey-resume-dismissed';

const JourneyResume = ({ className = '' }) => {
  const [state, setState] = useState({ stage: null, project: null, projectViews: 0 });
  const [dismissed, setDismissed] = useState(true);
  const [armed, setArmed] = useState(false);
  const [changing, setChanging] = useState(false);
  const { count } = useFavourites();
  const { isLoggedIn } = useMarketingAuth();
  const authModal = useAuthModal();

  useEffect(() => {
    const resolved = resolveJourney();
    setState(resolved);
    try { setDismissed(window.sessionStorage.getItem(DISMISS_KEY) === '1'); }
    catch { setDismissed(false); }

    // Behaviour-triggered: appear only once the visitor has shown intent,
    // so it feels earned rather than naggy. Strong signal (2+ project
    // views or saved guides) arms immediately; otherwise arm on a scroll
    // depth or a short dwell.
    if (resolved.projectViews >= 2 || count > 0) {
      setArmed(true);
      return;
    }
    let done = false;
    const arm = () => { if (!done) { done = true; setArmed(true); cleanup(); } };
    const onScroll = () => {
      const sc = window.scrollY / Math.max(1, document.body.scrollHeight - window.innerHeight);
      if (sc > 0.35) arm();
    };
    const timer = setTimeout(arm, 6000);
    window.addEventListener('scroll', onScroll, { passive: true });
    function cleanup() { clearTimeout(timer); window.removeEventListener('scroll', onScroll); }
    return cleanup;
  }, [count]);

  const dismiss = () => {
    setDismissed(true);
    try { window.sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  };

  // Low-confidence path: we know the project but not the stage. Rather
  // than guess, ask one gentle question; the tap seeds the stage.
  const pickStage = (key) => {
    try {
      window.localStorage.setItem(JOURNEY_STORAGE_KEY, key);
      window.localStorage.setItem(JOURNEY_DECLARED_KEY, '1');
    } catch { /* ignore */ }
    recordStageSignal(key);
    setChanging(false);
    setState((s) => ({ ...s, stage: key, declared: key, confidence: 'high' }));
  };

  const { stage, project, declared, confidence } = state;
  // We "know" the stage only if the visitor explicitly declared it. If we
  // merely inferred it from a page visit, we ask rather than guess.
  const needsStage = Boolean(project && !declared);
  const hasProject = Boolean(project && stage && declared);
  const hasSaves = count > 0;
  // Confidence is "soft" when we declared a stage but the visitor's
  // browsing hints at a different one (medium). We hedge the copy and
  // offer a quick correction rather than asserting.
  const softMatch = hasProject && confidence === 'medium';
  // Show the stage picker either when we never learned a stage, or when
  // the visitor asked to change a soft guess.
  const showQuestion = needsStage || (changing && hasProject);

  if (dismissed || !armed || (!hasProject && !needsStage && !hasSaves)) return null;

  // Primary next step comes from the remembered project + stage.
  const nextStep = hasProject ? (getNextSteps(project.slug, stage)[0] || null) : null;
  const stageIdx = hasProject ? IMPROVEMENT_STAGES.findIndex((s) => s.key === stage) : -1;

  let headline;
  let sub;
  if (showQuestion) {
    headline = project ? `Where are you up to with your ${project.label.toLowerCase()}?` : 'Where are you in your journey?';
    sub = 'Tell us and we will point you to the right next step.';
  } else if (softMatch) {
    headline = `Continue your ${project.label.toLowerCase()} plan`;
    sub = `Looks like you are around the ${stageLabel(stage).toLowerCase()} stage. Here is a good next step if so.`;
  } else if (hasProject) {
    headline = `Pick up your ${project.label.toLowerCase()} plan`;
    sub = `You told us you are ${stageLabel(stage).toLowerCase()}. Here is your next step.`;
  } else {
    headline = 'Pick up where you left off';
    sub = `You have ${count} saved guide${count === 1 ? '' : 's'} waiting for you.`;
  }

  const card = (
    <motion.aside
      className={`fixed bottom-4 left-4 right-4 sm:right-auto z-[55] w-auto sm:w-[340px] max-w-[calc(100vw-2rem)] rounded-2xl bg-[#0a2540] shadow-2xl ring-1 ring-white/10 ${className}`}
      data-testid="journey-resume"
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      role="complementary"
      aria-label="Resume your journey"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 inline-flex items-center justify-center w-7 h-7 rounded-full text-white/45 hover:text-white hover:bg-white/10 transition-colors"
        data-testid="journey-resume-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="p-4 pr-9">
        <div className="min-w-0">
          <p className="text-white font-semibold text-[14px] leading-snug">{headline}</p>
          <p className="text-white/55 text-[12px] leading-snug mt-0.5">{sub}</p>
          {!showQuestion && stageIdx >= 0 && (
            <div className="flex items-center gap-1 mt-2" data-testid="journey-resume-progress" aria-label={`Step ${stageIdx + 1} of ${IMPROVEMENT_STAGES.length}`}>
              {IMPROVEMENT_STAGES.map((s, i) => (
                <span
                  key={s.key}
                  className={`h-1.5 rounded-full transition-all ${i <= stageIdx ? 'bg-[#1d8efe] w-4' : 'bg-white/20 w-2'}`}
                />
              ))}
            </div>
          )}
        </div>

        {showQuestion ? (
          <div className="flex flex-wrap gap-1.5 mt-3.5" data-testid="journey-resume-question">
            {IMPROVEMENT_STAGES.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => pickStage(s.key)}
                className="inline-flex items-center px-3 py-1.5 rounded-full text-[12px] font-medium bg-white/10 text-white border border-white/15 hover:bg-white hover:text-[#0a2540] transition-colors"
                data-testid={`journey-resume-stage-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-3 mt-3.5">
            {nextStep && (
              <Link
                to={nextStep.href}
                className="inline-flex items-center gap-1.5 bg-white text-[#0a2540] px-3.5 py-2 rounded-full text-[12.5px] font-semibold hover:bg-white/90 transition-colors whitespace-nowrap"
                data-testid="journey-resume-continue"
              >
                {nextStep.title}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
            {softMatch && (
              <button
                type="button"
                onClick={() => setChanging(true)}
                className="inline-flex items-center text-white/70 hover:text-white text-[12px] font-medium underline-offset-2 hover:underline whitespace-nowrap"
                data-testid="journey-resume-change-stage"
              >
                Not quite? Change stage
              </button>
            )}
            {hasSaves && !softMatch && (
              <Link
                to="/my-buildpilot"
                className="inline-flex items-center text-white/70 hover:text-white text-[12px] font-medium underline-offset-2 hover:underline whitespace-nowrap"
                data-testid="journey-resume-saved"
              >
                View {count} saved
              </Link>
            )}
            {!isLoggedIn && !hasSaves && !softMatch && (
              <button
                type="button"
                onClick={() => authModal.open('signup', { returnY: window.scrollY })}
                className="inline-flex items-center text-white/70 hover:text-white text-[12px] font-medium underline-offset-2 hover:underline whitespace-nowrap"
                data-testid="journey-resume-signup"
              >
                Save across devices
              </button>
            )}
          </div>
        )}
      </div>
    </motion.aside>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(card, document.body);
};

export default JourneyResume;
